'use client';

/**
 * Tabs Component
 *
 * A flexible tabs component with multiple visual variants.
 *
 * ## Variants
 *
 * Pass `variant` prop to `TabsList`:
 *
 * - **boxed** - Tabs with muted background container (default)
 * - **pill** - Fully rounded tabs with muted background
 * - **bordered** - Transparent background with bottom border indicator
 * - **lifted** - Browser-tab style with active tab lifted above border
 *
 * ## Usage
 *
 * ```tsx
 * // Boxed variant (default)
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="boxed">
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *   </TabsList>
 * </Tabs>
 *
 * // Bordered variant with counts
 * <Tabs defaultValue="all">
 *   <TabsList variant="bordered">
 *     <TabsTriggerWithCount value="all" count={5} isActive>All</TabsTriggerWithCount>
 *     <TabsTriggerWithCount value="mine" count={3}>Mine</TabsTriggerWithCount>
 *   </TabsList>
 * </Tabs>
 *
 * // With icons
 * <Tabs defaultValue="home">
 *   <TabsList variant="boxed" size="md">
 *     <TabsTrigger value="home" icon={<Home />}>Home</TabsTrigger>
 *     <TabsTrigger value="settings" icon={<Settings />}>Settings</TabsTrigger>
 *   </TabsList>
 * </Tabs>
 *
 * // Pill variant with gradient
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="pill" gradient>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   </TabsList>
 * </Tabs>
 *
 * // Animated indicator (smooth sliding underline)
 * <Tabs defaultValue="tab1">
 *   <TabsList variant="bordered">
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *     <TabsIndicator />
 *   </TabsList>
 * </Tabs>
 * ```
 *
 * @module Tabs
 */

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Badge } from '@/components/atoms/display/Badge';
import {
  TabsContent,
  TabsIndicator,
  tabsListVariants,
  tabsTriggerVariants,
} from '@/components/ui/tabs';
import { useControllableState } from '@/hooks/use-controllable-state';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

export interface TabsProps
  extends Omit<TabsPrimitive.Root.Props, 'onValueChange'> {
  /** Controlled active tab value. When provided, component is controlled. */
  value?: string;
  /** Initial active tab for uncontrolled mode */
  defaultValue?: string;
  /** Callback when active tab changes */
  onValueChange?: (value: string) => void;
}

export interface TabsListProps
  extends TabsPrimitive.List.Props,
    VariantProps<typeof tabsListVariants> {
  /** Enable gradient styling on active tab (boxed/pill variants only) */
  gradient?: boolean;
}

export interface TabsTriggerProps extends TabsPrimitive.Tab.Props {
  /** Icon element to render in the tab */
  icon?: React.ReactNode;
  /** Position of the icon relative to text */
  iconPosition?: 'start' | 'top';
  /** Show a notification dot indicator */
  dot?: boolean;
  /** Badge element to render after the text (e.g., count or status badge) */
  badge?: React.ReactNode;
}

export type TabsContentProps = TabsPrimitive.Panel.Props;

export type TabsIndicatorProps = TabsPrimitive.Indicator.Props;

export interface TabsTriggerWithCountProps extends TabsTriggerProps {
  /** Count to display in badge */
  count?: number;
  /** Whether this tab is currently active (used for badge styling) */
  isActive?: boolean;
}

// ============================================
// TabsRoot (with controlled/uncontrolled support)
// ============================================

function TabsRoot({
  value: controlledValue,
  defaultValue,
  onValueChange,
  orientation = 'horizontal',
  children,
  className,
  ...props
}: TabsProps) {
  const [value, setValue] = useControllableState({
    value: controlledValue,
    defaultValue: defaultValue ?? '',
    onChange: onValueChange,
  });

  const handleValueChange = React.useCallback(
    (newValue: string | null) => {
      if (newValue !== null) {
        setValue(newValue);
      }
    },
    [setValue],
  );

  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      value={value}
      onValueChange={handleValueChange}
      className={cn(
        'gap-2 group/tabs flex data-[orientation=horizontal]:flex-col',
        className,
      )}
      {...props}
    >
      {children}
    </TabsPrimitive.Root>
  );
}

// ============================================
// TabsList
// ============================================

function TabsList({
  className,
  variant = 'boxed',
  size = 'md',
  align = 'start',
  borderPosition = 'bottom',
  gradient,
  ...props
}: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      data-size={size}
      data-align={align}
      data-border-position={borderPosition}
      {...(gradient ? { 'data-gradient': '' } : {})}
      className={cn(
        tabsListVariants({ variant, size, align, borderPosition }),
        className,
      )}
      {...props}
    />
  );
}

// Trigger styles are provided by tabsTriggerVariants from the primitive (ui/tabs).
// The molecule layers only icon/dot/badge-specific classes on top.

// ============================================
// TabsTrigger
// ============================================

function TabsTrigger({
  className,
  icon,
  iconPosition = 'start',
  dot,
  badge,
  children,
  ...props
}: TabsTriggerProps) {
  const isIconOnly = icon && !children;

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      data-icon-position={iconPosition}
      data-icon-only={isIconOnly || undefined}
      className={cn(
        tabsTriggerVariants(),
        iconPosition === 'top' && 'flex-col gap-1 pb-6',
        isIconOnly && 'px-2',
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          data-slot="tabs-trigger-icon"
          className={cn(
            "shrink-0 [&_svg:not([class*='size-'])]:size-4",
            iconPosition === 'top' &&
              "[&_svg:not([class*='size-'])]:size-[18px]",
          )}
        >
          {icon}
        </span>
      )}
      {children}
      {badge}
      {dot && (
        <span
          data-slot="tabs-trigger-dot"
          className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary"
        />
      )}
    </TabsPrimitive.Tab>
  );
}

// ============================================
// TabsTriggerWithCount - Extended trigger with badge count
// ============================================

/**
 * Extended tab trigger with an optional count badge.
 * Best used with the `bordered` variant for filter-style tabs.
 *
 * @example
 * ```tsx
 * <TabsList variant="bordered">
 *   <TabsTriggerWithCount value="all" count={5} isActive>All Meetings</TabsTriggerWithCount>
 *   <TabsTriggerWithCount value="my" count={4}>My Meetings</TabsTriggerWithCount>
 *   <TabsTriggerWithCount value="shared" count={1}>Shared with me</TabsTriggerWithCount>
 * </TabsList>
 * ```
 */
function TabsTriggerWithCount({
  children,
  count,
  isActive,
  className,
  ...props
}: TabsTriggerWithCountProps) {
  return (
    <TabsTrigger className={cn('gap-2', className)} {...props}>
      {children}
      {count !== undefined && (
        <Badge
          variant="soft"
          color={isActive ? 'primary' : 'secondary'}
          size="xs"
          className="min-w-[18px]"
        >
          {count}
        </Badge>
      )}
    </TabsTrigger>
  );
}

// ============================================
// Display Names
// ============================================

TabsRoot.displayName = 'Tabs';
TabsList.displayName = 'TabsList';
TabsTrigger.displayName = 'TabsTrigger';
TabsContent.displayName = 'TabsContent';
TabsIndicator.displayName = 'TabsIndicator';
TabsTriggerWithCount.displayName = 'TabsTriggerWithCount';

// ============================================
// Compound Component (Dot Notation)
// ============================================

/**
 * Tabs compound component with dot notation access to sub-components.
 *
 * @example
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <Tabs.List>
 *     <Tabs.Trigger value="tab1">Tab 1</Tabs.Trigger>
 *     <Tabs.Trigger value="tab2">Tab 2</Tabs.Trigger>
 *   </Tabs.List>
 *   <Tabs.Content value="tab1">Content 1</Tabs.Content>
 *   <Tabs.Content value="tab2">Content 2</Tabs.Content>
 * </Tabs>
 * ```
 */
const Tabs = Object.assign(TabsRoot, {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
  Indicator: TabsIndicator,
  TriggerWithCount: TabsTriggerWithCount,
});

export {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsTrigger,
  TabsTriggerWithCount,
  tabsListVariants,
  tabsTriggerVariants,
};
